/**
 * WebRTCSessionStore — reactive state container for the WebRTC hook.
 *
 * Centralizes all React-visible state (status, error, role, sessionState,
 * reconnectFailed, inputError, cursorHidden, authRequired) in a single
 * class that implements the observer pattern via `subscribe()` and exposes
 * its current snapshot via `getSnapshot()`. The hook consumes it with
 * `useSyncExternalStore`.
 *
 * Why: previously the hook had 8 independent useState calls, each one
 * causing a re-render and forcing a re-run of every useCallback that
 * depended on the state setter. By centralizing state in this class:
 *   1. Setter identity is stable (no need for useCallback wrappers).
 *   2. State transitions can be observed and tested in isolation
 *      (no React render required to assert on state).
 *   3. The hook becomes a thin React adapter; imperative WebRTC logic
 *      (PC, data channel, signaling) can later be moved into a separate
 *      class without touching this store.
 *
 * The store is a plain class — no React dependencies. This keeps it
 * trivially testable: instantiate, call setters, subscribe, assert on
 * snapshots.
 */

import type { MultiUserSessionState, SessionRole, WebRTCStatus } from '@/types/webrtc';

export type Status = WebRTCStatus;

export interface InputErrorInfo {
  action: string;
  message: string;
  count: number;
  sampled: boolean;
  at: number;
}

export interface InputStats {
  // sent: how many input events the frontend tried to put on the data
  // channel. Includes drops and backoff waits. Operators compare
  // against `dropped` to spot a saturated channel.
  sent: number;
  // dropped: how many input events the frontend gave up on after
  // exhausting the backpressure retry budget. Non-zero is a strong
  // signal that the data channel is overloaded (slow consumer, TURN
  // relay issues, network blip) — the user is clicking and the agent
  // is not receiving.
  dropped: number;
  // backoffWaits: how many times the frontend had to wait for the
  // data channel buffer to drain before sending. A consistently high
  // value (>50% of sent) means the channel is at the edge of its
  // capacity; combine with dropped for a full picture.
  backoffWaits: number;
  // lastPingRttMs: round-trip time of the one-shot self-test ping
  // fired on data channel open. Healthy <50ms, degraded 50-200ms,
  // broken >200ms. Null until the first self-test completes.
  lastPingRttMs: number | null;
  // lastPingAt: when the last self-test echo was received.
  lastPingAt: number | null;
}

export interface WebRTCSessionState {
  status: Status;
  error: string | null;
  authRequired: boolean;
  role: SessionRole;
  sessionState: MultiUserSessionState | null;
  reconnectFailed: boolean;
  // Number of automatic reconnects attempted since the last successful
  // connection. This is operator-facing, so it belongs in the reactive
  // snapshot rather than only in the hook's imperative ref.
  reconnectAttempt: number;
  inputError: InputErrorInfo | null;
  cursorHidden: boolean;
  // inputSessionVersion: monotonic counter from the agent's
  // `input.session` message (PR 3 fix). Bumps every time the input
  // data channel opens — including mid-session reconnects that keep
  // the React status as 'connected'. Consumers can use it as a
  // dependency in effects that need to re-sync state after a
  // reconnect (modifiers, cursor visibility, etc.).
  inputSessionVersion: number;
  // inputStats: per-session input traffic counters + last self-test
  // RTT (PR 5). Surfaced in the DebugPanel so operators can spot a
  // degraded data channel before any user click fails.
  inputStats: InputStats;
  // PR 6 (debug-visibility): live PeerConnection state. These four
  // fields are updated from the hook's `onconnectionstatechange` /
  // `oniceconnectionstatechange` / `onsignalingstatechange` /
  // `onicegatheringstatechange` callbacks. The WebRTCPage renders
  // them inline as a chip so operators can diagnose "no video" without
  // opening DevTools — a silent failure here is the typical symptom
  // of a binary mismatch (old agent on the wire that doesn't label its
  // video tracks "screen-N", so attachStream() can't find a target
  // <video>).
  pcConnectionState: RTCPeerConnectionState | null;
  pcIceConnectionState: RTCIceConnectionState | null;
  pcSignalingState: RTCSignalingState | null;
  pcIceGatheringState: RTCIceGatheringState | null;
  // tracksReceived: how many `ontrack` events fired since the last
  // session start. Useful to tell apart "agent never sent a track"
  // (0) from "agent sent a track but the label didn't match any pane"
  // (>0). lastTrackLabel is the most recent track's label so the
  // operator can see whether it carries the canonical "screen-N"
  // identifier the multi-pane routing expects.
  tracksReceived: number;
  lastTrackLabel: string | null;
  lastTrackKind: string | null;
}

const INITIAL_INPUT_STATS: InputStats = {
  sent: 0,
  dropped: 0,
  backoffWaits: 0,
  lastPingRttMs: null,
  lastPingAt: null,
};

// PR 6: initial values for the live PeerConnection state. Null
// (= "we haven't created a PC yet") is the right default so the
// inline chip can distinguish "not yet attempted" from a real
// state like "new" or "failed".
const INITIAL_PC_STATE = {
  pcConnectionState: null,
  pcIceConnectionState: null,
  pcSignalingState: null,
  pcIceGatheringState: null,
  tracksReceived: 0,
  lastTrackLabel: null,
  lastTrackKind: null,
} as const;

const INITIAL_STATE: WebRTCSessionState = {
  status: 'idle',
  error: null,
  authRequired: false,
  role: 'pending',
  sessionState: null,
  reconnectFailed: false,
  reconnectAttempt: 0,
  inputError: null,
  cursorHidden: false,
  inputSessionVersion: 0,
  inputStats: { ...INITIAL_INPUT_STATS },
  // PR 6: spread the live PC state into the initial snapshot.
  ...INITIAL_PC_STATE,
};

type Listener = () => void;

export class WebRTCSessionStore {
  private state: WebRTCSessionState = { ...INITIAL_STATE };
  private listeners: Set<Listener> = new Set();

  // Stable per-snapshot reference so `useSyncExternalStore` can use
  // referential equality to bail out of re-renders when nothing changed.
  private cachedSnapshot: WebRTCSessionState = this.state;

  // ----- Observer pattern (for useSyncExternalStore) -----

  // Stable references for useSyncExternalStore. `useSyncExternalStore`
  // requires these references to be stable across renders, so we bind
  // once in the constructor and expose the bound versions. Calling
  // `.bind(store)` in the hook on every render creates new function
  // references, which causes React to re-subscribe on every render
  // and silently drop notifications.
  readonly subscribe: (listener: Listener) => () => void = (listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getSnapshot: () => WebRTCSessionState = () => this.cachedSnapshot;

  // ----- Read access (also exposed for non-React consumers, e.g. tests) -----

  getStatus(): Status {
    return this.state.status;
  }
  getError(): string | null {
    return this.state.error;
  }
  isAuthRequired(): boolean {
    return this.state.authRequired;
  }
  getRole(): SessionRole {
    return this.state.role;
  }
  getSessionState(): MultiUserSessionState | null {
    return this.state.sessionState;
  }
  isReconnectFailed(): boolean {
    return this.state.reconnectFailed;
  }
  getReconnectAttempt(): number {
    return this.state.reconnectAttempt;
  }
  getInputError(): InputErrorInfo | null {
    return this.state.inputError;
  }
  isCursorHidden(): boolean {
    return this.state.cursorHidden;
  }
  getInputSessionVersion(): number {
    return this.state.inputSessionVersion;
  }
  getInputStats(): InputStats {
    return this.state.inputStats;
  }

  // ----- Mutators (immutable updates so reference changes are visible) -----
  //
  // All setters are arrow function class fields (not method shorthands)
  // so that they're auto-bound to the instance. This means callers can do
  // `const setX = store.setX; setX(value);` without losing `this`. This
  // is critical for `useSyncExternalStore` and for keeping React
  // dependency arrays stable.

  readonly setStatus: (status: Status) => void = (status) => {
    this.update((s) => (s.status === status ? s : { ...s, status }));
  };

  readonly setError: (error: string | null) => void = (error) => {
    this.update((s) => (s.error === error ? s : { ...s, error }));
  };

  readonly setAuthRequired: (authRequired: boolean) => void = (authRequired) => {
    this.update((s) => (s.authRequired === authRequired ? s : { ...s, authRequired }));
  };

  readonly setRole: (role: SessionRole) => void = (role) => {
    this.update((s) => (s.role === role ? s : { ...s, role }));
  };

  readonly setSessionState: (sessionState: MultiUserSessionState | null) => void = (
    sessionState
  ) => {
    this.update((s) => (s.sessionState === sessionState ? s : { ...s, sessionState }));
  };

  readonly setReconnectFailed: (reconnectFailed: boolean) => void = (reconnectFailed) => {
    this.update((s) => (s.reconnectFailed === reconnectFailed ? s : { ...s, reconnectFailed }));
  };

  readonly setReconnectAttempt: (reconnectAttempt: number) => void = (reconnectAttempt) => {
    this.update((s) => (s.reconnectAttempt === reconnectAttempt ? s : { ...s, reconnectAttempt }));
  };

  readonly setInputError: (inputError: InputErrorInfo | null) => void = (inputError) => {
    this.update((s) => (s.inputError === inputError ? s : { ...s, inputError }));
  };

  readonly setCursorHidden: (cursorHidden: boolean) => void = (cursorHidden) => {
    this.update((s) => (s.cursorHidden === cursorHidden ? s : { ...s, cursorHidden }));
  };

  readonly setInputSessionVersion: (version: number) => void = (version) => {
    this.update((s) =>
      s.inputSessionVersion === version ? s : { ...s, inputSessionVersion: version }
    );
  };

  // PR 5: input stats. The store tracks per-session input traffic
  // counters and the last self-test RTT. Bump helpers are stable
  // (arrow class fields) so callers can put them in dependency
  // arrays without re-renders.

  readonly bumpInputSent: (delta?: number) => void = (delta = 1) => {
    this.update((s) => ({
      ...s,
      inputStats: { ...s.inputStats, sent: s.inputStats.sent + delta },
    }));
  };

  readonly bumpInputDropped: (delta?: number) => void = (delta = 1) => {
    this.update((s) => ({
      ...s,
      inputStats: { ...s.inputStats, dropped: s.inputStats.dropped + delta },
    }));
  };

  readonly bumpInputBackoffWaits: (delta?: number) => void = (delta = 1) => {
    this.update((s) => ({
      ...s,
      inputStats: { ...s.inputStats, backoffWaits: s.inputStats.backoffWaits + delta },
    }));
  };

  readonly setInputPingRtt: (rttMs: number, at: number) => void = (rttMs, at) => {
    this.update((s) => ({
      ...s,
      inputStats: { ...s.inputStats, lastPingRttMs: rttMs, lastPingAt: at },
    }));
  };

  readonly resetInputStats: () => void = () => {
    this.update((s) => ({ ...s, inputStats: { ...INITIAL_INPUT_STATS } }));
  };

  // PR 6: live PeerConnection state. The hook calls these from the
  // onconnectionstatechange / oniceconnectionstatechange /
  // onsignalingstatechange / onicegatheringstatechange callbacks. We
  // keep the mutators single-purpose (one state field each) so the
  // hook can update them independently and the React component picks
  // up the change with referential equality (no spurious re-renders
  // when a sibling state field changes).

  readonly setPcConnectionState: (state: RTCPeerConnectionState | null) => void = (state) => {
    this.update((s) => (s.pcConnectionState === state ? s : { ...s, pcConnectionState: state }));
  };

  readonly setPcIceConnectionState: (state: RTCIceConnectionState | null) => void = (state) => {
    this.update((s) =>
      s.pcIceConnectionState === state ? s : { ...s, pcIceConnectionState: state }
    );
  };

  readonly setPcSignalingState: (state: RTCSignalingState | null) => void = (state) => {
    this.update((s) => (s.pcSignalingState === state ? s : { ...s, pcSignalingState: state }));
  };

  readonly setPcIceGatheringState: (state: RTCIceGatheringState | null) => void = (state) => {
    this.update((s) =>
      s.pcIceGatheringState === state ? s : { ...s, pcIceGatheringState: state }
    );
  };

  // PR 6: track reception counter. attachStream() calls this every
  // time the agent's ontrack fires so the inline chip can show
  // "tracks=0" when nothing is arriving (vs. "tracks=2" when the
  // agent is sending but the labels don't match a pane — that means
  // the agent is on a binary that doesn't follow the screen-N
  // convention and the operator should look at the agent version).
  readonly noteTrackReceived: (label: string | null, kind: string | null) => void = (
    label,
    kind
  ) => {
    this.update((s) => ({
      ...s,
      tracksReceived: s.tracksReceived + 1,
      lastTrackLabel: label,
      lastTrackKind: kind,
    }));
  };

  readonly resetPcState: () => void = () => {
    this.update((s) => ({ ...s, ...INITIAL_PC_STATE }));
  };

  /**
   * Compound update for the common "fatal error" transition. Equivalent
   * to setStatus('error') + setError(msg), but a single update avoids
   * a transient re-render with status='error' / error=null.
   */
  readonly setFatalError: (msg: string) => void = (msg) => {
    this.update((s) =>
      s.status === 'error' && s.error === msg ? s : { ...s, status: 'error', error: msg }
    );
  };

  /**
   * Reset everything to the initial state. Used on hard teardown
   * (e.g. when the hook unmounts or the user explicitly stops).
   */
  readonly reset: () => void = () => {
    this.update(() => ({ ...INITIAL_STATE }));
  };

  // ----- Internal -----

  private update(updater: (s: WebRTCSessionState) => WebRTCSessionState): void {
    const next = updater(this.state);
    if (next === this.state) return; // no-op
    this.state = next;
    this.cachedSnapshot = next;
    for (const listener of this.listeners) {
      try {
        listener();
      } catch (_e) {
        // a misbehaving listener shouldn't break the rest
      }
    }
  }
}

// Re-export for callers that need the underlying state machine type
export type { WebRTCStatus };

// ── Global input-stats mirror (PR 5) ───────────────────────────────────────
//
// The DebugPanel is a global component (mounted once in App.tsx) and
// cannot reach the per-hook WebRTCSessionStore instance. To surface
// the live inputStats without a React context refactor, the active
// session's hook pushes the latest stats into this module-level
// snapshot. The DebugPanel reads it on a low-frequency interval and
// re-renders. The interval is necessary because module-level state
// has no observer pattern by itself; a 1s cadence is enough for the
// diagnostic use case (operators are not staring at the panel in real
// time, they look at it when something is wrong).
//
// Trade-offs: ~1s display lag, no historical data, only one active
// session at a time. All acceptable for a diagnostic surface that
// already has its own backpressure / sampling semantics.
type StatsListener = (stats: InputStats) => void;

let latestInputStats: InputStats = {
  sent: 0,
  dropped: 0,
  backoffWaits: 0,
  lastPingRttMs: null,
  lastPingAt: null,
};
const statsListeners = new Set<StatsListener>();

export function setLatestInputStats(stats: InputStats): void {
  latestInputStats = stats;
  for (const l of statsListeners) {
    try {
      l(stats);
    } catch (_e) {
      // ignore
    }
  }
}

export function getLatestInputStats(): InputStats {
  return latestInputStats;
}

export function subscribeLatestInputStats(fn: StatsListener): () => void {
  statsListeners.add(fn);
  return () => {
    statsListeners.delete(fn);
  };
}
